import React, { Fragment, HTMLAttributes, useEffect, useRef, useState } from 'react';
import QuickLink from '../../../../basic/quickLink';
import Icon from '../../../../basic/icons';
import PersonalizedNotification from '../../../personalNotification';
import { useDevice } from '../../../../../providers/device-provider';
import Button from '../../../../basic/button';
import { useAuthentication } from '../../../../../common/hooks/useAuthentication';
import { useUserProfile } from '../../../../../providers/user-provider';
import { CartProps, useCartProvider } from '../../../../../providers/cart-provider';
import { ICart } from '../../../../../container/cart/ICart';
import Dropdown from '../../../../basic/dropdown';
import { useRouter } from '../../../../../common/hooks/useRouter';
import FlexDivider from '../../../../basic/flex-divider';
import ErrorMessage from '../../../errorMessage';
import { useTranslation } from 'react-i18next';
import {
  buildAnalytics,
  DataAnalyticsClicks,
  DataAnalyticsLocations,
  DataAnalyticsTypes,
} from '../../../../basic/analytics';
import { checkActiveUrl, getQueryParams } from '../../../../../utils/browserUtils';
import { INotificationErrMsg } from '../../IHeader';
import { useTranslator } from '../../locales-provider';
import { getRegionFromPath } from '../../../../../utils/regionUtils';
import { unwrapPLPQueryParams } from '../../../../../common/router/history';
import {
  PERSONALIZED_ALERT_AUTO_OPEN_KEY,
  PERSONALIZED_ALERT_AUTO_OPEN_ROUTE_KEY,
  PERSONALIZED_ALERT_DISMISSED_KEY,
} from '../../../../../utils/sessionStorageUtils';

export enum NavToolbarIcons {
  ShowAfterLogin = 'showafterlogin',
  Authenticated = 'authenticated',
  Cart = 'cart',
  Logout = 'signout',
  Notification = 'Notifications',
  None = 'None',
}
interface ExternalLinksProps extends HTMLAttributes<HTMLButtonElement> {
  href: string;
  label: string;
  divider?: boolean;
  type?: any;
  locations?: any;
  click?: any;
}
interface NotificationProps extends HTMLAttributes<HTMLButtonElement> {
  data: any;
  notificationErrorMsg: INotificationErrMsg[];
  divider?: boolean;
  stacked?: boolean;
  autoOpenOnly?: boolean;
}
interface ButtonProps extends HTMLAttributes<HTMLButtonElement> {
  data: any;
  type?: any;
  locations?: any;
  click?: any;
}
interface AccountMenuProps extends HTMLAttributes<HTMLButtonElement> {
  menuMap: any;
}
interface SearchProps extends HTMLAttributes<HTMLButtonElement> {
  searchBar: any;
}
interface SkipProps extends HTMLAttributes<HTMLButtonElement> {
  name: string;
}
const ExternalLinks: React.FC<ExternalLinksProps> = ({
  href,
  label,
  divider,
  type,
  locations,
  click,
}) => {
  return (
    <div className="flex">
      <QuickLink
        href={`${href}`}
        isExternal={true}
        className="text-sm lg:text-md xl:text-md text-white flex justify-center mr-3"
        dataAnalytics={buildAnalytics(locations, type)}
      >
        <span className="underline underline-offset-4">{label}</span>
        <Icon name="externalLink" className="ml-1 w-full text-sm md:text-lg lg:text-lg mt-1"></Icon>
      </QuickLink>
      {divider && (
        <FlexDivider
          className="my-1 !w-[1px] text-btn-base mr-3 h-4"
          alignment="vertical"
          color="bg-sky-300"
        />
      )}
    </div>
  );
};

export default ExternalLinks;

export const NotificationDialog: React.FC<NotificationProps> = ({
  data,
  divider,
  stacked = false,
  autoOpenOnly = false,
}) => {
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const { isMobile } = useDevice();
  const {
    userProfile,
    extendedDetailsLoading,
    notifications,
    notificationsLoading,
    notificationError,
    closeNotification,
  } = useUserProfile();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [autoOpenSignal, setAutoOpenSignal] = useState(0);

  const getHeaderLogoFocusTarget = () => {
    const headerLogoLinks = Array.from(
      document.querySelectorAll<HTMLElement>('[data-testid="header-logo-link"]'),
    );

    return headerLogoLinks.find((element) => {
      if (element.hasAttribute('hidden')) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;
      if (element.tabIndex === -1) return false;
      return typeof element.focus === 'function';
    });
  };

  const closeDialog = (signal: boolean) => {
    if (signal) {
      setShowNotificationDialog(false);
      sessionStorage.setItem(PERSONALIZED_ALERT_DISMISSED_KEY, 'true');
      sessionStorage.removeItem(PERSONALIZED_ALERT_AUTO_OPEN_KEY);
    }
    const headerLogoLink = getHeaderLogoFocusTarget();

    if (headerLogoLink) {
      headerLogoLink.focus();
      return;
    }

    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  };

  useEffect(() => {
    const handleAutoOpenSignal = () => setAutoOpenSignal(signal => signal + 1);
    window.addEventListener(PERSONALIZED_ALERT_AUTO_OPEN_KEY, handleAutoOpenSignal);
    return () => window.removeEventListener(PERSONALIZED_ALERT_AUTO_OPEN_KEY, handleAutoOpenSignal);
  }, []);

  useEffect(() => {
    const shouldAutoOpen = sessionStorage.getItem(PERSONALIZED_ALERT_AUTO_OPEN_KEY) === 'true';
    const dismissed = sessionStorage.getItem(PERSONALIZED_ALERT_DISMISSED_KEY) === 'true';
    const currentRoute = `${window.location.pathname}${window.location.search}`;
    const autoOpenRoute = sessionStorage.getItem(PERSONALIZED_ALERT_AUTO_OPEN_ROUTE_KEY);
    const isSecureHome = window.location.pathname.endsWith('/secure/home');
    const normalizeRoute = (route: string) => {
      const parts = route.split('/').filter(Boolean);
      return parts.length > 0 ? `/${parts.slice(1).join('/')}` : '/';
    };
    const isAllowedRoute =
      isSecureHome ||
      autoOpenRoute === currentRoute ||
      Boolean(autoOpenRoute && currentRoute.endsWith(autoOpenRoute)) ||
      normalizeRoute(autoOpenRoute || '') === normalizeRoute(currentRoute);

    if (
      !showNotificationDialog &&
      shouldAutoOpen &&
      !dismissed &&
      isAllowedRoute &&
      userProfile &&
      !extendedDetailsLoading &&
      notifications?.length > 0
    ) {
      setShowNotificationDialog(true);
      sessionStorage.removeItem(PERSONALIZED_ALERT_AUTO_OPEN_KEY);
      sessionStorage.removeItem(PERSONALIZED_ALERT_AUTO_OPEN_ROUTE_KEY);
    } else if (shouldAutoOpen && notifications?.length === 0) {
      sessionStorage.removeItem(PERSONALIZED_ALERT_AUTO_OPEN_KEY);
      sessionStorage.removeItem(PERSONALIZED_ALERT_AUTO_OPEN_ROUTE_KEY);
    }
  }, [autoOpenSignal, extendedDetailsLoading, notifications, showNotificationDialog, userProfile]);

  return (
    <div className="flex ">
      {!autoOpenOnly && (
        <button
          ref={buttonRef}
          onClick={() => setShowNotificationDialog(true)}
          className={stacked ? 'mr-3 flex flex-col items-center justify-center group' : 'mr-3'}
        >
          <div className={stacked ? 'relative flex flex-col items-center justify-center' : 'flex text-center'}>
          <Icon
            name="bell"
            aria-hidden="true"
            className={stacked
              ? 'text-2xl text-white'
              : `text-white mr-1xs hover:text-secondary-300 ${isMobile ? '' : 'w-full'} lg:text-md xl:text-md mt-1`}
          ></Icon>
          {notifications && notifications.length > 0 ? (
            <span
              className={`absolute pt-[2px] ${stacked ? 'right-[-6px] top-[-5px]' : isMobile ? 'ml-[-8px] mt-[-5px]' : 'ml-[-7px] mt-[-3px]'}  rounded-full w-4 h-4 p-0 m-0 leading-tight text-center align-middle text-white items-center 
          bg-rose-600 text-2xs`}
            >
              {notifications.length}
            </span>
          ) : null}
          <span
            className={stacked
              ? 'font-semibold text-xs group-hover:text-secondary-300 text-white whitespace-nowrap mt-1 px-4'
              : `text-sm lg:text-md xl:text-md text-white hover:text-secondary-300 ${isMobile ? '' : 'underline underline-offset-2'}`}
          >
            {data.label}
          </span>
          </div>
        </button>
      )}
      {showNotificationDialog && (
        <div
          className={`absolute ${isMobile ? 'top-0 right-0 left-0' : 'right-[26rem]'} top-[3.6rem] z-10`}
        >
          <PersonalizedNotification
            notificationError={notificationError}
            responseData={notifications}
            closeDialog={closeDialog}
            closeNotification={closeNotification}
            loadingNotification={notificationsLoading}
          />
        </div>
      )}
      {divider && (
        <FlexDivider
          className="my-1 !w-[1px] text-btn-base mr-3 h-4"
          alignment="vertical"
          color="bg-sky-300"
        />
      )}
    </div>
  );
};

export const HeaderToolbarButton: React.FC<ButtonProps> = ({ data, type, locations, click }) => {
  const { error, setError, loginRoute } = useAuthentication();
  const { userProfile } = useUserProfile();
  const { cart } = useCartProvider() as CartProps;
  const { cartItems }: ICart = cart || { cartItems: [] };
  const cartItemsCount = cart?.cartItems?.length || 0;
  const { t } = useTranslation();
  const regionPath = getRegionFromPath();

  // const handleClick = (icon: any, path: any) => {
  //   if (path === NavToolbarIcons.None) {
  //     return '';
  //   } else if (icon === 'signIn') {
  //     loginRoute();
  //     return '';
  //   }
  //   return path;
  // };

  return (
    <Fragment>
      <ErrorMessage visible={error?.length > 0} setVisible={setError} modal message={t(error)} />

      <div className="relative ">
        <Button
          onClick={() => {
            if (data.icon === NavToolbarIcons.Cart) {
              sessionStorage.setItem('cartActionForADA', '');
            }
            if (data.path) {
              const baseUrl = window.location.origin;
              window.location.href = `${baseUrl}/${regionPath}${data.path}`;
            }
          }}
          role="link"
          shape="rounded"
          border={true}
          className={`py-2 px-5 mr-md text-sm lg:text-md xl:text-md ${data.icon === 'cart' ? 'text-white border-white' : 'bg-btn-fill'}  text-btn-base border-btn-border hover:bg-btn-hover hover:text-btn-hover-text w-ful`}
          dataAnalytics={buildAnalytics(locations, type, [], DataAnalyticsClicks.Secure_Cart)}
        >
          {data.icon === NavToolbarIcons.Cart && userProfile && cartItemsCount > 0 ? (
            <span className="flex justify-center items-center absolute right-24 top-1 pt-px rounded-full bg-rose-600 w-4 h-4 p-0 m-0 text-2xs  leading-tight text-center align-middle text-white">
              {cart?.cartItemsCount}
            </span>
          ) : null}
          <Icon
            name={data.icon}
            className={`text-md md:text-md mr-1 w-full ${data.background !== 'null' ? '' : 'text-white '}`}
          />
          {data.label}
        </Button>
      </div>
    </Fragment>
  );
};
export const AccountMenu: React.FC<AccountMenuProps> = ({ menuMap }) => {
  const { navigate } = useRouter();
  const { handleLogout, error, setError } = useAuthentication();
  const [selectedMenuList, setselectedMenuList] = React.useState([]);
  const [selectedMenu, setselectedMenu] = React.useState<any>();
  const { t } = useTranslation();
  const [relativepath, setRelativePath] = useState<string>();
  const url = new URL(window.location.href);

  const onClickHandler = (menu: string) => {
    const findObj: any = selectedMenuList.find((item: any) => item.key === menu);
    setselectedMenu(findObj);
    if (findObj != null && findObj?.iconName === 'signout') {
      handleLogout();
    } else navigate(menu);
  };
  useEffect(() => {
    setRelativePath(url.pathname);
  }, [url]);
  useEffect(() => {
    let list: any = [];
    if (menuMap && menuMap.length > 0) {
      menuMap.map((menu: any) => {
        menu?.data?.forEach((element: any, index: any) => {
          list.push({ key: element.href, value: element.label, iconName: element?.icon });
        });
      });
      if (list && list.length > 0) {
        let status = false;
        list.map((item: any) => {
          if (checkActiveUrl(item.key, relativepath)) {
            status = true;
            setselectedMenu(item);
          }
        });
        if (!status) setselectedMenu(list[0]);

        setselectedMenuList(list);
      }
    }
  }, [menuMap]);

  return (
    <Fragment>
      <ErrorMessage visible={error?.length > 0} setVisible={setError} modal message={t(error)} />
      <Dropdown
        id="myaccountDropdownDesktop"
        options={selectedMenuList}
        clickHandler={onClickHandler}
        iconName="location"
        selected={selectedMenu}
        disabled={false}
        toolTipMessage={''}
        color="text-white"
        dropdownName="My Account"
      />
    </Fragment>
  );
};

export const AccountMobileMenu: React.FC<AccountMenuProps> = ({ menuMap }) => {
  const { navigate } = useRouter();
  const { handleLogout, error, setError } = useAuthentication();
  const { t } = useTranslation();

  const handleClick = (data: any) => {
    if (data?.icon === 'signout') {
      handleLogout();
    } else {
      navigate(data?.href);
    }
  };
  return (
    <div>
      <ErrorMessage visible={error?.length > 0} setVisible={setError} modal message={t(error)} />
      {menuMap != null &&
        menuMap?.map((menuData: any, index: any) => (
          <Fragment key={'menu-data-' + index}>
            {menuData?.data.map((data: any, li: any) => {
              return (
                <div
                  key={'data' + li}
                  className={`${data.icon === 'user' ? 'm-4 ml-0' : 'm-4 mt-0 ml-6'}`}
                >
                  <button
                    key={`hd-dt-tbb-${li}`}
                    className="flex flex-col  items-center justify-center group"
                    onClick={() => handleClick(data)}
                  >
                    <p className="flex justify-start text-white px-3 font-medium">
                      <Icon name={data.icon} className="mr-2 "></Icon>
                      <span
                        className={`text-sm ${data.icon === 'user' ? '' : 'underline underline-offset-2'} `}
                      >
                        {data.label}
                      </span>
                    </p>
                  </button>
                </div>
              );
            })}
          </Fragment>
        ))}
    </div>
  );
};

export const SearchButton: React.FC<SearchProps> = ({ searchBar }) => {
  const { userProfile } = useUserProfile();
  const { isMobile } = useDevice();
  const [popularSearch, setPopularSearch] = React.useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchtext, setSearchText] = React.useState<string>('');
  const queryParamFilters = unwrapPLPQueryParams();
  const simpleSearch = queryParamFilters['simpleSearch'] || '';
  const { navigate } = useRouter();
  const locales = useTranslator('global');
  const inputRef = useRef<HTMLInputElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setPopularSearch(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  const handleKeyDown = (event: any) => {
    if (event.key === 'Tab' && popularSearch) {
      // setPopularSearch(false);
    } else if (event.key === 'Escape') {
      setPopularSearch(false);
      setShowTooltip(false);
    }
  };
  useEffect(() => {
    setSearchText(typeof simpleSearch === 'string' ? simpleSearch : '');
  }, [simpleSearch]);

  const handleKeyEnter = (event: any) => {
    if (event.key === 'Enter') {
      plpPage();
    } else if (event.key === 'ArrowDown') {
      setPopularSearch(true);
    }
  };
  const plpPage = () => {
    if (searchBar && searchBar.simple_search && searchtext) {
      const url = `${searchBar.simple_search.href}?simpleSearch=${searchtext || ''}`;
      navigate(url);
    }
  };

  return (
    <Fragment>
      <div
        ref={dropdownRef}
        className="w-full flex justify-end items-end"
        onKeyDown={handleKeyDown}
      >
        <div
          className={`flex items-center ${userProfile && !isMobile ? 'w-3/4' : 'w-full'} relative`}
        >
          <div className={`w-full flex relative items-center justify-end`}>
            <input
              ref={inputRef}
              className="focus:ring-0 p-2 placeholder:text-sm rounded-md w-full"
              type="text"
              placeholder={`${searchBar?.help_text}` || ''}
              aria-label={`${searchBar?.help_text}, ${locales?.['header.simple.search.placceholder'] || ''}`}
              name="searchtext"
              value={decodeURIComponent(searchtext)}
              onChange={event => {
                setSearchText(event.target.value);
              }}
              onKeyDown={handleKeyEnter}
              onClick={() => setPopularSearch(true)}
              onMouseOver={() => setShowTooltip(true)}
              onFocus={() => setShowTooltip(true)}
              onBlur={() => setShowTooltip(false)}
              onMouseLeave={() => setShowTooltip(false)}
              data-testid="simple-search"
            />
            {showTooltip && !searchtext.length && !popularSearch && (
              <div
                className={`absolute bg-white drop-shadow-lg top-10 w-[202px] h-[72px] mt-[0.75rem] mr-[-5.5rem] p-[1rem] z-50 whitespace-normal text-sm font-normal text-black text-left rounded shadow-[0_5px_15px_0px_rgba(0,0,0,0.3)]`}
                data-testid="tooltip-test"
              >
                <div
                  className={`content-none absolute bg-white top-0 w-[1rem] h-[1rem] left-[35%] rotate-45 translate-y-[-50%] translate-x-[-50%] shadow-[-20px_-4px_25px_-4px_rgba(0,0,0,0.6)]`}
                ></div>
                {locales?.['header.simple.search.tooltip']
                  ? locales['header.simple.search.tooltip']
                  : t('header.simple.search.tooltip')}
              </div>
            )}
            {searchtext && (
              <Button
                aria-label={`${locales?.['header.simple.search.clear']}`}
                title={`${locales?.['header.simple.search.clear']}`}
                tabIndex={0}
                role="button"
                className="absolute right-2 border-secondary-300 text-secondary-300 rounded-full border-2 w-6 h-6"
                onClick={() => setSearchText('')}
                onKeyDown={event => {
                  if (event.code === 'Enter') setSearchText('');
                }}
              >
                <Icon name="close" className="text-secondary-300" />
              </Button>
            )}
          </div>
          {popularSearch && (
            <div
              data-testid="popular-search"
              role="search options"
              className={`absolute bg-white z-100 rounded-md  top-12 sm:w-[300px] md:w-[385px] ]`}
            >
              <div className="items-center w-full h-full p-sm shadow-[0px_4px_20px_0px_rgba(0,0,0,0.3)] ">
                <div className={`w-full `}>
                  <p className="text-md text-card-header font-semibold ml-2">
                    {searchBar?.search_title}
                  </p>
                  <div className="max-h-64 overflow-auto py-2">
                    {searchBar &&
                      searchBar?.popular_search &&
                      searchBar.popular_search?.map((searchOption: any, idx: any) => {
                        return (
                          <div key={idx} className="float-left">
                            <Button
                              link={`${searchOption.href}`}
                              shape="rounded"
                              border={true}
                              className="rounded-md text-black px-3 py-[10px] bg-[#F7F7F7] text-sm m-2 leading-tight"
                              aria-labelledby={`search-label-${searchOption.search_name}`}
                            >
                              <span id={`search-label-${searchOption.search_name}`}>
                                Search {searchOption.search_name}
                              </span>
                            </Button>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className={`flex justify-end ml-4`}>
            {searchBar && searchBar?.simple_search && (
              <Button
                role="link"
                shape="rounded"
                border={true}
                onClick={plpPage}
                onFocus={() => setPopularSearch(false)}
                className={`py-2 px-3 ${isMobile ? 'mr-0' : 'mr-md'} text-sm text-white border-white text-btn-base border-btn-border hover:bg-btn-hover hover:text-btn-hover-text w-ful `}
                dataAnalytics={buildAnalytics(
                  DataAnalyticsLocations.Header,
                  DataAnalyticsTypes.Button,
                )}
                data-testid="search-button"
              >
                {searchBar.simple_search?.title}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export const SkipButton: React.FC<SkipProps> = ({ name }) => {
  const skiptoMainContent = (e: any) => {
    e.preventDefault();
    document.getElementById('landing-page')?.focus();
  };
  return (
    <div>
      <button
        onClick={skiptoMainContent}
        className={`py-2 px-3  text-sm text-secondary-400 border-secondary-400 text-btn-base border-btn-border w-ful cursor-auto absolute left-3 top-sm`}
        aria-label={`${name}`}
      >
        {name}
      </button>
    </div>
  );
};
